import type {ContextPanelData} from 'common/widgets/context-panel/types';
import type {TooltipContextActionResult} from 'common/widgets/query-block/child-menu';
import type {Section, SectionItem} from 'common/components/items-menu/types';
import type {PropertyType, ResourceType} from 'common/report/constants';
import type {SpecialTimeUnit, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import type {CustomProperty} from 'common/report/stores/custom-properties';
import type {Cohort} from 'common/util/definitions-types';

import type * as PropertiesItemsMenu from './item-menu-types';

// Export the new PropertySectionItem type under the old PropertyItem name while we work on the SectionItems
// This is saner than touching the code of all callers.
export type PropertyItem = PropertiesItemsMenu.PropertySectionItem;

export enum PropertyItemAction {
  Typecast = `typecast`,
}

/**
 * These represents the serializable form of a PropertyItem.
 * This list should not contain any UI properties as is saved to the bookmark
 * Any properties should also be included in BookmarkablePropertyItemKeys
 */
export type BookmarkablePropertyItem = {
  customPropertyId?: number | string;
  customProperty?: CustomProperty;
  dataGroupId?: string;
  dimensionDataGroupId?: string;
  joinPropertyType?: PropertyType;
  propertyDefaultType: PropertyType;
  propertyObjectKey?: string | string[];
  resourceType: ResourceType;
  tempDataGroupId?: string;
  type: PropertyType;
  unit?: TimeUnit | SpecialTimeUnit;
  value?: any;
  label: string;
};

/**
 * This boolean map is used to filter any property item to only the keys in BookmarkablePropertyItem
 */
export const BookmarkablePropertyItemKeyMap: {[P in keyof Required<BookmarkablePropertyItem>]: true} = {
  customPropertyId: true,
  customProperty: true,
  dataGroupId: true,
  dimensionDataGroupId: true,
  joinPropertyType: true,
  propertyDefaultType: true,
  propertyObjectKey: true,
  resourceType: true,
  tempDataGroupId: true,
  type: true,
  unit: true,
  value: true,
  label: true,
};

export const NonDataGroupPropertyItemIdentifierKeys: Array<keyof PropertiesItemsMenu.PropertySectionItem> = [
  `customPropertyId`,
  `value`,
];

// Checked in order
export const PropertyItemPersistedIdentifierKeys: Array<keyof PropertiesItemsMenu.PropertySectionItem> = [
  `tempDataGroupId`,
  `dataGroupId`,
  ...NonDataGroupPropertyItemIdentifierKeys,
];

export enum PropertyCombineMode {
  Union = `union`,
  Intersection = `intersection`,
}

export type CohortItem = Cohort & {
  label: string;
  canView?: boolean;
  canUpdateBasic?: boolean;
  canShare?: boolean;
  isSuperadmin?: boolean;
};

// because PropertiesMenu is actually a class that implements this interface
export interface IPropertiesMenu {
  getStagedItemType(): StagedItemType;

  getSelectedTab(): SelectedTab;
  getSelectedHoverTooltipData(): ContextPanelData;
  getSelectedTrackingProps(): Obj;
  handleCloseMenu(): void;
  handleTooltipContextAction(ev: Event): TooltipContextActionResult;
  hasOpenedModal(): boolean;
  isContentSearchable(): boolean;
  getSearchFilter(): string;
  isCohortSelection(): boolean;
}

export type GetSortedMenuSections = (sections: Section[]) => Section[];

export enum StagedItemType {
  Typecast,
  Dimension,
  Datetime,
  Object,
  User,
  Group,
  Cohort,
  BrowseProperties,
}

/**
 * Defines all the different property types that this dropdown can render
 *
 * aims to replace the convoluted way of defining what this menu renders by
 * using attributes like `project-has-people`, `project-has-cohorts`, etc
 */
export enum MenuItemType {
  Events = `events`,
  EventCustomProperties = `event_custom_properties`,
  UserCustomProperties = `user_custom_properties`,
  LookupTableProperties = `lookup_table_properties`,
  EventProperties = `event_properties`,
  UserProperties = `user_properties`,
  Cohorts = `cohorts`,
  DataGroupProperties = `datagroup_properties`,
}

export const AllPropertiesTab = `all_properties`;
export const AllTab = `all`;
export const EventsInPropertiesMenuTab = `events`;
/**
 * selectedTab can be `all`, a dataGroupId or a resourceType
 */
export type SelectedTab =
  | typeof AllTab
  | typeof AllPropertiesTab
  | typeof EventsInPropertiesMenuTab
  | string
  | ResourceType;

enum IncludeCustomPropertyType {
  All = `all`,
  None = `none`,
  PersistedOnly = `persisted-only`,
  PersistedNonBehavioralOnly = `persisted-non-behavioral-only`,
}

export enum TypecastPillAction {
  /**
   * @deprecated Typecasting should be selected through the overflow menu. This value is reserved for uses that do not currently have an overflow menu.
   */
  Typecast = `typecast`,
  TriggerTutorial = `trigger-tutorial`,
}
export interface PropertiesMenuAttrs {
  'cohort-multiselect'?: boolean;
  'cohort-operation-type'?: string;
  'apply-button'?: string;
  'apply-button-disabled'?: boolean;
  'allow-behavioral-property-creation'?: boolean;
  'allow-typecast-pill-action'?: TypecastPillAction;
  'enable-workspace-filter-restrictions'?: boolean;
  'disabled-items'?: Obj;
  'include-custom-properties'?: IncludeCustomPropertyType;
  'exclude-dimension-properties'?: boolean;
  'exclude-lookup-table-properties'?: boolean;
  'force-old-dropdowns'?: boolean;
  'property-combine-mode'?: PropertyCombineMode;
  'show-all-tab'?: boolean;
  'show-all-users-cohort'?: boolean;
  'show-items-caret'?: boolean;
  'single-data-group'?: string;
  'other-options'?: string[];
  'show-select-all'?: boolean;
  'select-all-disabled'?: boolean;
  selected?: SelectedPropertyItem;
  'hide-recently-viewed'?: boolean;
  'hide-section-labels'?: boolean;
  'selectable-sensitive-properties'?: boolean;
  'description-edit-disabled'?: boolean;
  'menu-item-types'?: MenuItemType[];
}

// export type SelectedPropertyItem<ItemT = PropertyItemSectionItem | EventItem> = {
export type SelectedPropertyItem = {
  item: BookmarkablePropertyItem | BookmarkablePropertyItem[];
  rawItem: SectionItem;
  autoTriggered: boolean;
  commit?: boolean;
  menuItemType?: MenuItemType;
  onItemClicked?: boolean;
};

export type SelectPropertyEvent = CustomEvent<SelectedPropertyItem>;

export type PropertiesMenuJsxProps = {
  attrs: PropertiesMenuAttrs;
  on?: {
    select?: (ev: SelectPropertyEvent) => void;
  };
};

export enum SortBy {
  Suggested,
  Alphabetical,
}

export enum SortOrder {
  Ascending = `asc`,
  Descending = `desc`,
}

export type SortConfig = {by: SortBy; order: SortOrder} | {by: SortBy.Suggested};
export const SortOptions: {[label: string]: SortConfig} = {
  Suggested: {by: SortBy.Suggested},
  'Alpha A → Z': {by: SortBy.Alphabetical, order: SortOrder.Ascending},
  'Alpha Z → A': {by: SortBy.Alphabetical, order: SortOrder.Descending},
};
